/**
 * Phase 6 — Close
 *
 * Claude generates a 3-5 sentence session summary:
 * what they brought, what they saw, what they chose.
 * Tone: quiet, respectful, final. A record, not a pep talk.
 *
 * After the summary animates in, a closing ceremony plays:
 * the spiral completes, and they're offered a return home.
 *
 * UIState flow:
 *   what-changed → generating → summary → grounding (conditional) → ritual
 *
 * Grounding is shown when intensity ≥ 7 OR emotion is heavy (grief/shame/anger/
 * helplessness/despair/terror/dread). Sage requirement: don't leave people in an
 * activated state — the pre-session warning sets expectation; this delivers aftercare.
 */
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { useScreenTrail } from '../../lib/sessionBack'
import Retained from '../ui/Retained'
import type { ScreenKey } from '../../lib/screenIds'
import { useTranslation } from 'react-i18next'
import { localizeEmotion, localizeEmotionLabel } from '../../data/emotions.pl'
import { SERIES_DATA } from '../../data/series'
import { localizeSeries } from '../../data/series.pl'
import { useNavigate } from 'react-router-dom'
import { db, supabase } from '../../lib/supabase'
import { callClaude } from '../../lib/claude'
import { classifyArchetypes } from '../../lib/archetypeClassifier'
import { extractSignals } from '../../lib/signalExtractor'
import { parseOptions } from '../../lib/parseOptions'
import { EMOTIONS } from '../../data/emotions'
import { trackSessionCompleted } from '../../lib/analytics'
import { reportSessionCompleted } from '../../lib/notifications/client'
import type { SessionMessage, SessionMode } from '../../types/session'
import LoadingSpiral from '../ui/LoadingSpiral'
import IntuitiveChoice from '../ui/IntuitiveChoice'
import type { ChoiceState } from '../ui/IntuitiveChoice'
import GroundingExercise from './GroundingExercise'

/**
 * Maps dominant emotions to relevant arcs for the suggestion card at Close.
 * arcId values align with SERIES_DATA ids in src/data/series.ts so these can
 * eventually deep-link into the actual arc when available.
 *
 * Cross-arc logic: if the user is already inside a series session (currentSeriesId
 * is set), matchArc filters out the arc they're already in and adjusts the card copy
 * to "connects with" rather than "go deeper" (no upgrade pitch needed for premium users).
 */
const ARC_MAP: Array<{ emotions: string[]; arcName: string; arcId: string; tagline: string }> = [
  {
    // Aligned to SERIES_DATA id 'grief' → "Through Loss"
    emotions: ['grief', 'loss', 'sadness', 'heartache', 'despair', 'longing'],
    arcName: 'Through Loss',
    arcId: 'grief',
    tagline: 'Go deeper into what you\'re mourning — at your own pace.',
  },
  {
    // Aligned to SERIES_DATA id 'shame' → "Old Enough to Know Better"
    // Covers guilt, self-criticism, worthlessness — all shame-family emotions
    emotions: ['shame', 'self-criticism', 'worthlessness', 'guilt', 'self-doubt', 'embarrassment'],
    arcName: 'Old Enough to Know Better',
    arcId: 'shame',
    tagline: 'A guided arc for healing the story you hold about yourself.',
  },
  {
    // Aligned to SERIES_DATA id 'forgiveness' → "The Forgiveness Work"
    // Anger/resentment/betrayal are the entry point for that arc
    emotions: ['anger', 'resentment', 'betrayal', 'bitterness', 'blame', 'frustration', 'hurt'],
    arcName: 'The Forgiveness Work',
    arcId: 'forgiveness',
    tagline: 'Move through resentment toward something that feels like release.',
  },
  {
    // Aligned to SERIES_DATA id 'boundary' → "The Boundary"
    // Fear/anxiety/helplessness often signal boundary collapse or self-abandonment
    emotions: ['fear', 'anxiety', 'overwhelm', 'tension', 'helplessness', 'dread', 'loneliness', 'disconnection', 'rejection', 'abandonment', 'isolation'],
    arcName: 'The Boundary',
    arcId: 'boundary',
    tagline: 'Explore the patterns keeping you at a distance from yourself and others.',
  },
  {
    // Aligned to SERIES_DATA id 'inner-critic' → "Meeting the Inner Critic"
    // Self-doubt + inadequacy patterns often trace back to the inner critic
    emotions: ['self-doubt', 'inadequacy', 'not enough', 'judging', 'criticism', 'perfectionism'],
    arcName: 'Meeting the Inner Critic',
    arcId: 'inner-critic',
    tagline: 'Understand the voice that judges you most harshly — and what it\'s protecting.',
  },
]

/**
 * Returns the first matching arc for the given emotion string, or null.
 * Emotion field can be a comma-separated multi-select string from Arrival.
 * When currentSeriesId is set, excludes the arc the user is already in.
 */
function matchArc(emotion: string, currentSeriesId?: string) {
  const lower = emotion.toLowerCase()
  for (const entry of ARC_MAP) {
    if (currentSeriesId && entry.arcId === currentSeriesId) continue  // don't suggest the current arc
    if (entry.emotions.some(e => lower.includes(e))) return entry
  }
  return null
}

/**
 * Emotions that warrant showing referral language at Close.
 * Sage requirement: any session that touches these at high intensity should offer
 * a pointer to professional support — it's the clinical liability floor.
 */
const REFERRAL_EMOTIONS = ['shame', 'grief', 'helplessness', 'despair', 'worthlessness', 'suicidal', 'hopeless']

/**
 * Emotions that trigger the grounding exercise interstitial (Sage spec).
 * These, combined with intensity ≥ 7, indicate the nervous system needs an
 * orienting cue before re-entering ordinary life.
 */
const HEAVY_EMOTIONS = ['grief', 'shame', 'anger', 'helplessness', 'despair', 'terror', 'dread', 'loss', 'sadness', 'worthlessness']

const CHANGED_OPTIONS_FALLBACK = [
  'A sense of lightness',
  'More space in my body',
  'A quiet knowing',
  'More compassion for myself',
  'A feeling of release',
  'A softening around this',
  'A feeling of being witnessed',
  'A greater honesty with myself',
  'A small but real shift',
]


interface CloseProps {
  sessionId: string
  emotion: string
  intensity: number
  storyText: string
  bodyLocation: string
  reminderText: string
  /** From Feel's Meaning Imprint — enriches session summary context. */
  beliefText?: string
  /** From Feel's Expression Step — enriches session summary context. */
  expressionText?: string
  patternReflection: string
  reframeResponse: string
  choiceText: string
  /**
   * The person's own words for the age of the feeling, from the optional field
   * on the Feel reflection; '' when they gave none. Sent to the guide as user
   * data (see lib/age.ts for what counts as an answer).
   */
  ageText?: string
  firstName?: string
  lifePathNumber?: number
  seriesContext?: string
  /**
   * SERIES_DATA id of the arc the user is currently inside (e.g. 'grief', 'boundary').
   * When set, the arc suggestion card uses cross-arc framing ("connects with X") instead
   * of the standard upgrade pitch, and filters out the current arc from suggestions.
   */
  currentSeriesId?: string
  /**
   * Mind-level lens — passed through to Claude so the Edge Function's case 6
   * prompt can differentiate summaries by lens. Shadow Gratitude framing is
   * always-on and applied unconditionally on the server.
   */
  sessionMode?: SessionMode
  /**
   * Asked once, when the person leaves Close and the session is marked finished:
   * true the first time, false ever after. The person can leave, come back, and
   * leave again, but the session only finishes once, so the things that count a
   * finished session (the pattern tally, archetype and signal extraction, the
   * 72-hour-wait notice) must not run again. Defaults to always true.
   */
  claimCompletion?: () => boolean
  /**
   * The summary already saved for this session, when it is resumed after one was
   * written. Close then opens on the summary (6.2) showing it, and asks the guide
   * for nothing: no new summary and no new "what changed" options.
   */
  initialSummary?: string
  onComplete: (summary: string) => void
}

/**
 * UIState flow:
 *   what-changed → generating → summary → grounding (if shouldGround) → ritual
 *
 * 'grounding' is a new interstitial screen between summary and ritual.
 * Renders the GroundingExercise component. Controlled by shouldGround,
 * which is derived from arrival intensity and emotion type.
 */
type UIState = 'what-changed' | 'generating' | 'summary' | 'grounding' | 'ritual' | 'error'

/** Which numbered screen each state shows (see lib/screenIds.ts). */
const SCREEN_OF: Record<UIState, ScreenKey | null> = {
  'what-changed': 'close.changed',
  'generating':   'close.summary',
  'summary':      'close.summary',
  // The grounding exercise is on screen in Close's place and reports its own number.
  'grounding':    null,
  'ritual':       'close.ritual',
  'error':        'close.error',
}

async function detectAndSavePattern(sessionId: string, patternReflection: string) {
  if (!patternReflection || !supabase) return
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return

  const result = await callClaude({
    phase: 12,
    emotion: '',
    intensity: 0,
    messages: [{ role: 'user', content: '[Extract label]' }],
    optionContext: patternReflection,
  })
  if ('error' in result || !result.response.trim()) return

  const label = result.response.trim().toLowerCase().replace(/["'.,\n]/g, '').trim()
  if (!label || label.length > 60) return

  const { data: existing } = await db()
    .from('patterns')
    .select('id, occurrence_count, session_ids')
    .eq('user_id', user.id)
    .eq('pattern_label', label)
    .maybeSingle()

  if (existing) {
    const ex = existing as { id: string; occurrence_count: number; session_ids: string[] }
    await db()
      .from('patterns')
      .update({
        occurrence_count: ex.occurrence_count + 1,
        last_seen_at: new Date().toISOString(),
        session_ids: [...new Set([...ex.session_ids, sessionId])],
      })
      .eq('id', ex.id)
  } else {
    await db()
      .from('patterns')
      .insert({
        user_id: user.id,
        pattern_label: label,
        occurrence_count: 1,
        session_ids: [sessionId],
      })
  }
}

export default function Close({
  sessionId,
  emotion,
  intensity,
  storyText,
  bodyLocation,
  reminderText,
  beliefText,
  expressionText,
  patternReflection,
  reframeResponse,
  choiceText,
  ageText,
  firstName = '',
  lifePathNumber,
  seriesContext,
  currentSeriesId,
  sessionMode,
  claimCompletion = () => true,
  initialSummary,
  onComplete,
}: CloseProps) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  /*
   * The suggested-arc card shows an arc name and a tagline. The NAME is not
   * translated here — ARC_MAP.arcId matches a SERIES_DATA id, so the already
   * reviewed Polish from series.pl.ts is reused instead of maintaining a second
   * translation of the same six arc names that could drift out of sync.
   *
   * The tagline is specific to this card and has no equivalent in the arc data,
   * so it comes from the locale keyed by the same arcId.
   */
  const arcNameFor = (arcId: string, fallback: string) => {
    const series = SERIES_DATA.find(s => s.id === arcId)
    return series ? localizeSeries(series, i18n.language).title : fallback
  }
  const arcTaglineFor = (arcId: string, fallback: string) => {
    const key = `close.arcTaglines.${arcId}`
    const value = t(key)
    // t() returns the key itself when it is missing — never show that.
    return value === key ? fallback : value
  }

  const changedFallback = (() => {
    const v = t('close.changedOptions', { returnObjects: true })
    return Array.isArray(v) && v.length === CHANGED_OPTIONS_FALLBACK.length
      ? (v as string[]) : CHANGED_OPTIONS_FALLBACK
  })()

  const [uiState, setUiState] = useState<UIState>(initialSummary ? 'summary' : 'what-changed')
  useScreenId(SCREEN_OF[uiState])
  const [summary, setSummary] = useState<string | null>(initialSummary || null)
  const [releaseText, setReleaseText] = useState('')
  const [welcomeText, setWelcomeText] = useState('')
  const [whatChanged, setWhatChanged] = useState('')
  const [changedOptions, setChangedOptions] = useState<string[]>([])
  const [optionsLoading, setOptionsLoading] = useState(true)
  // Intensity check-in: user rates how the feeling sits now vs. when they arrived
  const [closeIntensity, setCloseIntensity] = useState(intensity)
  // Favorite: mark this session as a key insight from the ritual screen
  const [isFavorite, setIsFavorite] = useState(false)

  // The grounding exercise, once opened, stays alive (hidden) when the person
  // moves on to the ritual, so Back from the ritual returns to the exercise's
  // last screen as it was left.
  // What the person chose on 6.1. Kept here, not in the picker, so it
  // outlives the picker: Back to this screen shows the same chips and words, and
  // Continue with nothing touched hands on the same answer (#68).
  const [changedPick, setChangedPick] = useState<ChoiceState | undefined>()

  const [groundingOpened, setGroundingOpened] = useState(false)

  // What the summary was written for. Going back to "what changed" and
  // continuing with the same answer shows that summary again; a different
  // answer writes a new one.
  const summaryFor = useRef<string | null>(null)
  // The reminder is scheduled once per session; a resumed saved summary was already reminded.
  const reminded = useRef(!!initialSummary)

  // ─── Back ──────────────────────────────────────────────────────────────────
  // The screens Close has shown, in order (lib/sessionBack.ts). 'grounding' is
  // one of them: the exercise hosted there has its own screens and is asked first.
  useScreenTrail<UIState>({
    screen: uiState,
    go: setUiState,
    kind: s => (s === 'generating' ? 'loading' : s === 'error' ? 'error' : 'rest'),
  })

  /**
   * Whether to show the grounding interstitial before the ritual screen.
   * Sage spec: trigger on intensity ≥ 7 OR any heavy/activating emotion.
   * This is derived once from props and never changes mid-session.
   */
  const lower = emotion.toLowerCase()
  const shouldGround = intensity >= 7 || HEAVY_EMOTIONS.some(e => lower.includes(e))

  /**
   * Whether to show the referral language at the bottom of the ritual screen.
   * Clinical floor (Sage): intensity ≥ 8 AND a referral-class emotion.
   * Copy is non-alarming — it's a gentle pointer, not a crisis intervention.
   */
  const shouldShowReferral = intensity >= 8 && REFERRAL_EMOTIONS.some(e => lower.includes(e))

  useEffect(() => {
    // Resumed on a saved summary: nothing is asked of the guide. If the person goes
    // Back to "what changed", the fixed list is there.
    if (initialSummary) {
      setChangedOptions(changedFallback)
      setOptionsLoading(false)
      return
    }
    callClaude({
      phase: 20, emotion, intensity,
      messages: [{ role: 'user', content: '[Generate options]' }],
      firstName, sessionId, seriesContext,
      optionContext: `Question: t('close.changedQuestion') Pattern they named: "${patternReflection}". They chose: "${choiceText}". Generate 9 options for what may have shifted — specific to what this person worked through and what they chose. VARY the structure: some are body sensations, some are relational shifts, some are inner states ("A small permission I hadn't given myself before"), some are cognitive ("I can see it differently now"). Each option must be a plausible response to this specific pattern + choice — not generic post-session phrases. No two options should feel like the same type of shift.`,
    }).then(result => {
      if (!('error' in result)) {
        const parsed = parseOptions(result.response)
        if (parsed.length >= 7) setChangedOptions(parsed)
        else setChangedOptions(changedFallback)
      } else {
        setChangedOptions(changedFallback)
      }
      setOptionsLoading(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (uiState !== 'generating') return
    const changedContext = whatChanged ? ` What shifted: ${whatChanged}.` : ''
    const beliefContext = beliefText ? ` Core belief surfaced: "${beliefText}".` : ''
    const expressionContext = expressionText ? ` Uncensored expression: "${expressionText}".` : ''
    const msgs: SessionMessage[] = [
      { role: 'user', content: storyText },
      { role: 'assistant', content: `I hear you. You feel this in your ${bodyLocation}. It reminds you of: ${reminderText}.${beliefContext}${expressionContext}` },
      { role: 'user', content: '[Pattern confirmed]' },
      { role: 'assistant', content: patternReflection },
      { role: 'user', content: reframeResponse },
      { role: 'assistant', content: t('close.thatIsYours') },
      { role: 'user', content: `${choiceText}${changedContext}` },
      { role: 'user', content: '[Generate session summary]' },
    ]

    callClaude({ phase: 6, emotion, intensity, messages: msgs, firstName, sessionId, lifePathNumber, seriesContext, sessionMode, ageText: ageText || undefined }).then(async result => {
      if ('error' in result) { setUiState('error'); return }
      const res = result.response
      setSummary(res)
      summaryFor.current = whatChanged

      // Save the summary as soon as it is written, so it is never lost. This does
      // NOT mark the session complete: that happens when the person leaves Close
      // (completeSession below). Marking it here meant a person who reached the
      // summary, went Back and changed an answer, or reloaded, had a session the
      // app no longer offered to resume and that a free account had already
      // counted against its three (#71).
      await db()
        .from('sessions')
        .update({ session_summary: res, phase_reached: 6 })
        .eq('id', sessionId)
      // The summary is what uses up a free session (lib/freeTier.ts), so the
      // "next session is open" reminder is scheduled now, not when the person leaves.
      // Once, however often the summary is rewritten. The write above comes first:
      // the reminder counts sessions that have a summary and must see this one.
      if (!reminded.current) {
        reminded.current = true
        void reportSessionCompleted(sessionId)
      }
      setUiState('summary')
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState])

  /**
   * The person is leaving Close: mark the session complete, then count it.
   *
   * This is the one place a session becomes complete. Everything that reads
   * `is_complete` (the free-tier gate on the client and in `facilitate`, the
   * "next session is open" reminder, Home's completed count and its "session in
   * progress" card, the Journal) therefore agrees on the same moment.
   *
   * The write comes first and the counting after it, because the reminder
   * counts completed sessions and must see this one. If the write fails the
   * person still leaves (they should not be trapped on a screen), the session
   * stays unfinished so the app offers it again, and nothing is counted. The
   * claim is taken only after a successful write, so that a later leave can
   * still finish it.
   */
  async function completeSession() {
    const { error } = await db()
      .from('sessions')
      .update({ is_complete: true, phase_reached: 6 })
      .eq('id', sessionId)
    if (error) {
      console.warn('[close] could not mark the session complete:', error.message)
      return
    }
    // Everything below counts a FINISHED session, so it happens once even if the
    // person leaves, comes back and leaves again.
    if (!claimCompletion()) return
    trackSessionCompleted(6)

    // Fire-and-forget: extract pattern label and upsert to patterns table
    void detectAndSavePattern(sessionId, patternReflection)

    // Fire-and-forget: classify archetypes for Awakening-tier users
    void classifyArchetypes(sessionId, storyText, patternReflection, choiceText, emotion, intensity)

    // Fire-and-forget: extract pattern signals for paid-tier users (feeds nightly clustering)
    void extractSignals({
      sessionId,
      storyText,
      patternReflection,
      choiceText,
      bodyLocation,
      beliefText: beliefText ?? '',
      emotion,
      intensity,
    })
  }

  // A second press while the first is still writing does nothing.
  const leaving = useRef(false)
  /** Finish the session, then do what the button was for. */
  async function leave(then: () => void) {
    if (leaving.current) return
    leaving.current = true
    try {
      await completeSession()
    } finally {
      leaving.current = false
    }
    then()
  }

  const today = new Date().toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' })
  // The healing phrase and the integration action are per-emotion copy with a
  // Polish side in emotions.pl.ts. They were read from the English record
  // directly, so a Polish reader got both in English on the summary (6.2).
  // localizeEmotion returns the English record untouched for English.
  const emotionRecord = EMOTIONS.find(e => e.word === emotion)
  const emotionData = emotionRecord ? localizeEmotion(emotionRecord, i18n.language) : undefined
  const healingPhrase = emotionData?.healingPhrase ?? null
  const integrationAction = emotionData?.integration ?? null

  /** The screen for the current state. The grounding exercise is drawn separately, below. */
  function renderScreen(): ReactNode {
    // The exercise is drawn by the slot below.
    if (uiState === 'grounding') return null

    if (uiState === 'what-changed') {
      return (
        <div className="flex-1 flex flex-col page-enter">
          <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">
            <IntuitiveChoice
              initialState={changedPick}
              onStateChange={setChangedPick}
              prompt={t('close.changedQuestion')}
              options={changedOptions}
              isLoadingOptions={optionsLoading}
              placeholder={t('close.orDescribe')}
              ctaLabel={t('session.continue')}
              skipLabel={t('close.skipContinue')}
              onComplete={value => {
                // Same answer after going back: the summary already written for it
                // is still right, and writing it again would also save it again.
                if (summary !== null && summaryFor.current === value) {
                  setUiState('summary')
                  return
                }
                setWhatChanged(value)
                setUiState('generating')
              }}
            />
          </div>
        </div>
      )
    }

    if (uiState === 'error') {
      return (
        <div className="flex-1 flex flex-col items-center justify-center px-6 page-enter">
          <p className="text-sm mb-6" style={{ color: '#6B5F7A' }}>{t('close.summaryError')}</p>
          <button
            onClick={() => onComplete('')}
            className="px-6 py-3 rounded-xl text-sm"
            style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
          >
            {t('close.continueAnyway')}
          </button>
        </div>
      )
    }

    return (
      <div className="flex-1 flex flex-col page-enter">
        <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

          {/* Generating */}
          {uiState === 'generating' && (
            <div className="flex-1 flex flex-col items-center justify-center">
              <LoadingSpiral size={28} label={t('close.witnessing')} />
            </div>
          )}

          {/* Summary */}
          {uiState === 'summary' && summary && (
            <div className="flex-1 flex flex-col message-appear">

              <p className="text-xs font-medium uppercase tracking-widest mb-6" style={{ color: '#9B72C0' }}>
                {t('session.phases.close')}
              </p>

              {/* The witness card */}
              <div
                className="mb-8 px-6 py-7 rounded-2xl"
                style={{
                  background: 'linear-gradient(145deg, #F9F5FF 0%, #F1E8F8 100%)',
                  border: '1.5px solid #CEBCDC',
                  boxShadow: '0 4px 24px rgba(61,31,98,0.06)',
                }}
              >
                <p className="text-xs font-medium uppercase tracking-widest mb-5" style={{ color: '#9B72C0' }}>
                  {t('close.whatHappened')}
                </p>
                <p className="text-base leading-relaxed font-display" style={{ color: '#1C1128', fontStyle: 'italic' }}>
                  {summary}
                </p>
                <div style={{ width: '40px', height: '1px', background: 'rgba(61,31,98,0.12)', margin: '1.5rem 0 1rem' }} />
                <p className="text-xs" style={{ color: '#9B8FAA' }}>
                  {today} · {localizeEmotionLabel(emotion, i18n.language)}
                </p>
              </div>

              {/* The choice, echoed */}
              <div
                className="mb-10 px-5 py-4 rounded-xl text-center"
                style={{ background: '#3D1F62' }}
              >
                <p className="text-xs font-medium uppercase tracking-widest mb-2"
                  style={{ color: 'rgba(255,255,255,0.45)' }}>
                  {t('close.todayIChoose')}
                </p>
                <p className="font-display text-base leading-relaxed"
                  style={{ color: 'white', fontStyle: 'italic', fontWeight: 400 }}>
                  {choiceText}
                </p>
              </div>

              {/* Witness Line — mirrors the journey: what they carried in, what they're leaving with */}
              <p
                className="mb-8 text-center font-display italic message-appear"
                style={{ color: '#9B72C0', lineHeight: 1.6, fontSize: '0.9375rem' }}
              >
                {t('close.witnessLine', { emotion: localizeEmotionLabel(emotion, i18n.language), choice: choiceText })}
              </p>

              {/* Healing phrase — earned here, after the work */}
              {healingPhrase && (
                <div
                  className="mb-4 px-5 py-4 rounded-xl text-center message-appear"
                  style={{
                    // Violet-tinted background — Close phase accent at ~4% opacity
                    background: 'rgba(155,114,192,0.04)',
                    // Left-border accent recipe for small inline context cards
                    borderTop: '1.5px solid #E4D5EE',
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '3px solid #9B72C0',
                  }}
                >
                  <p className="text-sm leading-relaxed font-display italic" style={{ color: '#6B3FA0' }}>
                    {healingPhrase}
                  </p>
                </div>
              )}

              {/* Integration action — one concrete thing to carry into the world */}
              {integrationAction && (
                <div
                  className="mb-6 px-5 py-4 rounded-xl message-appear"
                  style={{
                    // Keep white base — accent comes from the violet left border
                    background: 'white',
                    // Left-border accent recipe for small inline context cards
                    borderTop: '1.5px solid #E4D5EE',
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '3px solid #9B72C0',
                  }}
                >
                  <p className="text-xs font-medium uppercase tracking-widest mb-2" style={{ color: '#9B8FAA' }}>
                    {t('close.oneThingToDo')}
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#3D1F62', lineHeight: 1.65 }}>
                    {integrationAction}
                  </p>
                </div>
              )}

              {/* Intensity check-in — before/after delta */}
              <div
                className="mb-6 px-5 py-5 rounded-xl message-appear"
                style={{
                  background: 'white',
                  borderTop: '1.5px solid #E4D5EE',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '3px solid #C45272',
                }}
              >
                <p className="text-xs font-medium uppercase tracking-widest mb-1" style={{ color: '#C45272' }}>
                  {t('close.intensityCheck')}
                </p>
                <p className="text-sm mb-4" style={{ color: '#6B5F7A', lineHeight: 1.6 }}>
                  {t('close.arrivedAt')} <strong style={{ color: '#3D1F62' }}>{intensity}/10</strong>. {t('close.whereNow')}
                </p>
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={closeIntensity}
                  onChange={e => setCloseIntensity(Number(e.target.value))}
                  className="w-full mb-3"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: '#9B8FAA' }}>{t('close.scaleLow')}</span>
                  <span className="text-2xl font-display" style={{ color: '#3D1F62' }}>{closeIntensity}</span>
                  <span className="text-xs" style={{ color: '#9B8FAA' }}>{t('close.scaleHigh')}</span>
                </div>
                {closeIntensity < intensity && (
                  <p className="text-sm text-center mt-3 message-appear" style={{ color: '#9B72C0', fontStyle: 'italic' }}>
                    {intensity - closeIntensity >= 5
                      ? t('close.downFromBig', { from: intensity, to: closeIntensity })
                      : t('close.downFromSmall', { from: intensity, to: closeIntensity })
                    }
                  </p>
                )}
                {closeIntensity > intensity && (
                  <p className="text-sm text-center mt-3 message-appear" style={{ color: '#9B72C0', fontStyle: 'italic' }}>
                    {t('close.upTo', { to: closeIntensity })}
                  </p>
                )}
                {closeIntensity === intensity && (
                  <p className="text-sm text-center mt-3" style={{ color: '#C4B5D4', fontStyle: 'italic' }}>
                    {t('close.stillAt', { n: intensity })}
                  </p>
                )}
              </div>

              {/* Arc suggestion card — contextual nudge based on session emotion.
                  When inside a series (currentSeriesId set): cross-arc framing, no upgrade pitch.
                  When standalone session: upgrade pitch toward Awakening tier. */}
              {(() => {
                const arc = matchArc(emotion, currentSeriesId)
                if (!arc) return null

                // Cross-arc framing for users already in an Awakening arc
                const isInSeries = !!currentSeriesId
                return (
                  <div
                    className="mb-6 px-5 py-4 rounded-xl message-appear"
                    style={{
                      background: 'rgba(61,31,98,0.03)',
                      border: '1.5px solid #D4C0E8',
                    }}
                  >
                    <p className="text-xs font-medium uppercase tracking-widest mb-2" style={{ color: '#9B72C0' }}>
                      {isInSeries ? t('close.connectedArc') : t('close.goDeeper')}
                    </p>
                    <p className="text-sm font-medium mb-1" style={{ color: '#3D1F62' }}>
                      {arcNameFor(arc.arcId, arc.arcName)}
                    </p>
                    <p className="text-xs leading-relaxed mb-3" style={{ color: '#6B5F7A' }}>
                      {isInSeries
                        ? t('close.connectsWell')
                        : arcTaglineFor(arc.arcId, arc.tagline)
                      }
                    </p>
                    {!isInSeries && (
                      <p className="text-xs" style={{ color: '#9B8FAA' }}>
                        {t('close.availableAwakening')}
                      </p>
                    )}
                  </div>
                )
              })()}

              <div className="flex-1" />

              {/* Route through grounding if this was a heavy session (Sage requirement) */}
              <button
                onClick={() => {
                  if (shouldGround) setGroundingOpened(true)
                  setUiState(shouldGround ? 'grounding' : 'ritual')
                }}
                className="w-full py-4 rounded-xl font-medium tracking-wide"
                style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                {t('session.continue')}
              </button>
            </div>
          )}

          {/* Ritual of Release — own screen */}
          {uiState === 'ritual' && (
            <div className="flex-1 flex flex-col message-appear">
              <p className="text-xs font-medium uppercase tracking-widest mb-6" style={{ color: '#9B72C0' }}>
                {t('close.ritual')}
              </p>

              <div className="flex flex-col gap-5">
                <div className="rounded-2xl px-5 py-5" style={{
                  background: 'rgba(155,114,192,0.04)',
                  borderTop: '3px solid #9B72C0',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '1.5px solid #E4D5EE',
                }}>
                  <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>
                    {t('close.releaseQuestion')}
                  </p>
                  <textarea
                    value={releaseText}
                    onChange={e => setReleaseText(e.target.value)}
                    placeholder={t('close.releasePlaceholderShort')}
                    rows={2}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
                    style={{ borderColor: '#9B72C0', background: 'white', color: '#1C1128' }}
                    onFocus={e => { e.target.style.borderColor = '#9B72C0'; e.target.style.boxShadow = '0 0 0 3px rgba(155,114,192,0.18)' }}
                    onBlur={e => { e.target.style.borderColor = '#9B72C0'; e.target.style.boxShadow = 'none' }}
                  />
                </div>

                <div className="rounded-2xl px-5 py-5" style={{
                  background: 'rgba(155,114,192,0.04)',
                  borderTop: '3px solid #9B72C0',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '1.5px solid #E4D5EE',
                }}>
                  <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>
                    {t('close.welcomeQuestion')}
                  </p>
                  <textarea
                    value={welcomeText}
                    onChange={e => setWelcomeText(e.target.value)}
                    placeholder={t('close.welcomePlaceholderShort')}
                    rows={2}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
                    style={{ borderColor: '#9B72C0', background: 'white', color: '#1C1128' }}
                    onFocus={e => { e.target.style.borderColor = '#9B72C0'; e.target.style.boxShadow = '0 0 0 3px rgba(155,114,192,0.18)' }}
                    onBlur={e => { e.target.style.borderColor = '#9B72C0'; e.target.style.boxShadow = 'none' }}
                  />
                </div>
              </div>

              <div className="flex-1" />

              <p className="text-sm text-center mb-6" style={{ color: '#6B5F7A', lineHeight: 1.6 }}>
                {t('close.iShowed')}
              </p>

              {/* Referral language — clinical floor (Sage spec).
                  Shown for high-intensity + referral-class emotions (intensity ≥ 8 + shame/grief/helplessness etc.).
                  Not alarming — a quiet pointer, not a crisis intervention. No external links. */}
              {shouldShowReferral && (
                <div
                  className="mb-5 px-4 py-3 rounded-xl message-appear"
                  style={{
                    background: 'rgba(61,31,98,0.03)',
                    border: '1.5px solid #D4C0E8',
                  }}
                >
                  <p className="text-xs leading-relaxed text-center" style={{ color: '#6B5F7A', lineHeight: 1.65 }}>
                    {t('close.referral')}
                  </p>
                </div>
              )}

              {/* Mark as favorite */}
              <button
                type="button"
                onClick={async () => {
                  const next = !isFavorite
                  setIsFavorite(next)
                  await db().from('sessions').update({ is_favorite: next }).eq('id', sessionId)
                }}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-medium mb-3 transition-all"
                style={{
                  background: isFavorite ? 'rgba(184,125,10,0.08)' : 'rgba(155,114,192,0.05)',
                  border: `1.5px solid ${isFavorite ? 'rgba(184,125,10,0.4)' : '#E4D5EE'}`,
                  color: isFavorite ? '#B87D0A' : '#9B8FAA',
                  cursor: 'pointer',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill={isFavorite ? '#B87D0A' : 'none'} stroke={isFavorite ? '#B87D0A' : 'currentColor'} strokeWidth="1.5">
                  <polygon points="7,1 9,5 13,5.5 10,8.5 10.5,13 7,11 3.5,13 4,8.5 1,5.5 5,5" strokeLinejoin="round" />
                </svg>
                {isFavorite ? t('session.savedKeyInsight') : t('session.saveKeyInsight')}
              </button>

              <button
                onClick={() => void leave(() => onComplete(summary!))}
                className="w-full py-4 rounded-xl font-medium tracking-wide mb-3"
                style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                {t('choose.completeSession')}
              </button>

              {/* "Explore this further" — same-topic session shortcut.
                  Navigates to /session with the current emotion pre-selected so Arrival
                  skips the blank-slate picker. Emotion string is passed as-is (may be
                  comma-separated multi-select — Arrival.tsx splits and validates). */}
              <button
                onClick={() => void leave(() => navigate('/session', { state: { preselectedEmotion: emotion } }))}
                className="w-full py-3 rounded-xl text-sm font-medium"
                style={{
                  background: 'transparent',
                  border: '1.5px solid #E4D5EE',
                  color: '#6B5F7A',
                  cursor: 'pointer',
                }}
              >
                {t('close.exploreFurther')} →
              </button>
            </div>
          )}

        </div>
      </div>
    )
  }

  return (
    <>
      {renderScreen()}

      {/* Grounding interstitial — shown between summary and ritual for heavy sessions.
          GroundingExercise handles its own screen transitions (choosing → exercise → done).
          onDismiss advances to ritual regardless of which exercise was chosen/skipped.
          Kept alive once opened so that Back from the ritual returns to the
          exercise as it was left. It is wrapped in the same container every Close
          screen sits in, so it is laid out exactly as it was. */}
      {groundingOpened && (
        <Retained active={uiState === 'grounding'}>
          <div className="flex-1 flex flex-col page-enter">
            <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">
              <GroundingExercise onDismiss={() => setUiState('ritual')} />
            </div>
          </div>
        </Retained>
      )}
    </>
  )
}
